import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { 
  Check, 
  X,
  Clock,
  CalendarIcon,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  CheckCircle,
  Archive,
  FileText,
  Filter,
  Send
} from 'lucide-react';
import { format, parseISO, startOfWeek, addWeeks, subWeeks, addDays, isBefore, startOfDay } from 'date-fns';
import { fr, de, enUS } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReportingPlanning() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const locale = language === 'de' ? de : language === 'en' ? enUS : fr;
  const isManager = user?.role === 'manager' || user?.role === 'directeur';
  
  // States for collaborator view
  const [currentWeek, setCurrentWeek] = useState(startOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 }));
  const [weekSchedule, setWeekSchedule] = useState([]);
  const [weekReporting, setWeekReporting] = useState(null);
  const [archivedWeeks, setArchivedWeeks] = useState([]);
  const [editingSlot, setEditingSlot] = useState(null);
  const [hoursDialogOpen, setHoursDialogOpen] = useState(false);
  const [actualHours, setActualHours] = useState({ start: '', end: '', comment: '' });
  
  // States for manager view
  const [pendingReports, setPendingReports] = useState([]);
  const [allReports, setAllReports] = useState([]);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [userFilter, setUserFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState(null);
  const [team, setTeam] = useState([]);
  
  const [loading, setLoading] = useState(true);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(isManager ? 'pending' : 'report');

  const days = Array.from({ length: 7 }, (_, i) => addDays(currentWeek, i));
  const weekStart = format(currentWeek, 'yyyy-MM-dd');
  const weekEnd = format(addDays(currentWeek, 6), 'yyyy-MM-dd');
  const today = startOfDay(new Date());
  const isPastWeek = isBefore(addDays(currentWeek, 6), today);

  // Fetch collaborator's week schedule
  const fetchWeekSchedule = useCallback(async () => {
    if (isManager) return;
    try {
      const res = await axios.get(`${API}/schedule`, { 
        params: { week_start: weekStart, user_id: user?.id } 
      });
      setWeekSchedule(res.data);
      
      // Fetch reporting status for this week
      const reportRes = await axios.get(`${API}/reporting/week`, { 
        params: { week_start: weekStart } 
      });
      setWeekReporting(reportRes.data);
    } catch (err) {
      console.error('Error fetching schedule:', err);
    } finally {
      setLoading(false);
    }
  }, [weekStart, user?.id, isManager]);

  // Fetch archived weeks for collaborator
  const fetchArchivedWeeks = useCallback(async () => {
    if (isManager) return;
    try {
      const res = await axios.get(`${API}/reporting/archived`);
      setArchivedWeeks(res.data);
    } catch (err) {
      console.error('Error fetching archived weeks:', err);
    }
  }, [isManager]);

  // Fetch pending reports for managers
  const fetchPendingReports = useCallback(async () => {
    if (!isManager) return;
    try {
      const params = { status: 'pending' };
      const res = await axios.get(`${API}/reporting/all`, { params });
      setPendingReports(res.data);
    } catch (err) {
      console.error('Error fetching pending reports:', err);
    }
  }, [isManager]);

  // Fetch all reports for managers with filters
  const fetchAllReports = useCallback(async () => {
    if (!isManager) return;
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (userFilter !== 'all') params.user_id = userFilter;
      if (dateFilter) params.week_start = format(dateFilter, 'yyyy-MM-dd');
      
      const res = await axios.get(`${API}/reporting/all`, { params });
      setAllReports(res.data);
    } catch (err) {
      console.error('Error fetching reports:', err);
    } finally {
      setLoading(false);
    }
  }, [isManager, statusFilter, userFilter, dateFilter]);

  // Fetch team for manager filters
  const fetchTeam = useCallback(async () => {
    if (!isManager) return;
    try {
      const res = await axios.get(`${API}/team`);
      setTeam(res.data);
    } catch (err) {
      console.error('Error fetching team:', err);
    }
  }, [isManager]);

  useEffect(() => {
    if (isManager) {
      fetchTeam();
      fetchPendingReports();
      fetchAllReports();
    } else {
      fetchWeekSchedule();
      fetchArchivedWeeks();
    }
  }, [isManager, fetchTeam, fetchPendingReports, fetchAllReports, fetchWeekSchedule, fetchArchivedWeeks]);

  // Navigation
  const handlePrevWeek = () => {
    const newWeek = subWeeks(currentWeek, 1);
    setCurrentWeek(newWeek);
  };
  
  const handleNextWeek = () => {
    const newWeek = addWeeks(currentWeek, 1);
    // Don't allow going to current or future weeks
    if (isBefore(addDays(newWeek, 6), today)) {
      setCurrentWeek(newWeek);
    }
  };

  const handleDateSelect = (date) => {
    const weekStart = startOfWeek(date, { weekStartsOn: 1 });
    if (isBefore(addDays(weekStart, 6), today)) {
      setCurrentWeek(weekStart);
    }
    setCalendarOpen(false);
  };

  // Open hours dialog for a slot
  const openHoursDialog = (slot) => {
    setEditingSlot(slot);
    setActualHours({
      start: slot.actual_start || slot.start_time,
      end: slot.actual_end || slot.end_time,
      comment: slot.reporting_comment || ''
    });
    setHoursDialogOpen(true);
  };

  // Save actual hours for a slot
  const saveActualHours = async () => {
    if (!editingSlot) return;
    try {
      await axios.put(`${API}/reporting/slot/${editingSlot.id}`, {
        actual_start: actualHours.start,
        actual_end: actualHours.end,
        comment: actualHours.comment
      });
      toast.success(t('hoursSaved'));
      setHoursDialogOpen(false);
      fetchWeekSchedule();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error'));
    }
  };

  // Submit week for validation
  const submitWeekForValidation = async () => {
    try {
      await axios.post(`${API}/reporting/submit-week`, { week_start: weekStart });
      toast.success(t('weekSubmitted'));
      fetchWeekSchedule();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error'));
    }
  };

  // Manager: Approve/Reject a week report
  const handleReviewReport = async (reportId, status) => {
    try {
      await axios.put(`${API}/reporting/${reportId}/review`, { status });
      toast.success(status === 'approved' ? t('reportApproved') : t('reportRejected'));
      fetchPendingReports();
      fetchAllReports();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error'));
    }
  };

  // Calculate hours for a slot
  const calculateHours = (start, end) => {
    const startMin = parseInt(start.split(':')[0]) * 60 + parseInt(start.split(':')[1]);
    const endMin = parseInt(end.split(':')[0]) * 60 + parseInt(end.split(':')[1]);
    return ((endMin - startMin) / 60).toFixed(2);
  };

  // Calculate difference
  const calculateDifference = (planned, actual) => {
    const diff = parseFloat(actual) - parseFloat(planned);
    const sign = diff >= 0 ? '+' : '';
    return `${sign}${diff.toFixed(2)}h`;
  };

  // Calculate week totals
  const calculateWeekTotals = () => {
    let plannedTotal = 0;
    let actualTotal = 0;
    
    weekSchedule.forEach(slot => {
      const planned = parseFloat(calculateHours(slot.start_time, slot.end_time)) - (slot.break_minutes || 0) / 60;
      const actual = slot.actual_start && slot.actual_end 
        ? parseFloat(calculateHours(slot.actual_start, slot.actual_end)) - (slot.break_minutes || 0) / 60
        : planned;
      plannedTotal += planned;
      actualTotal += actual;
    });
    
    return { planned: plannedTotal.toFixed(2), actual: actualTotal.toFixed(2) };
  };

  // Generate time slots
  const generateTimeSlots = () => {
    const slots = [];
    for (let h = 6; h < 22; h++) {
      for (let m = 0; m < 60; m += 15) {
        slots.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
      }
    }
    return slots;
  };
  
  const TIME_SLOTS = generateTimeSlots();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // ==================== COLLABORATOR VIEW ====================
  if (!isManager) {
    const weekTotals = calculateWeekTotals();
    const canSubmit = isPastWeek && (!weekReporting || weekReporting.status === 'draft');
    const isSubmitted = weekReporting?.status === 'pending';
    const isValidated = weekReporting?.status === 'approved';
    const isRejected = weekReporting?.status === 'rejected';

    return (
      <div className="space-y-6" data-testid="reporting-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-3">
            <FileText className="h-8 w-8 text-primary" />
            <div>
              <h1 className="text-3xl">{t('reportingPlanning').toUpperCase()}</h1>
              <p className="text-muted-foreground text-sm">{t('reportYourHours')}</p>
            </div>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="report" className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              {t('reportHours')}
            </TabsTrigger>
            <TabsTrigger value="archived" className="flex items-center gap-2">
              <Archive className="h-4 w-4" />
              {t('archived')}
            </TabsTrigger>
          </TabsList>

          {/* Report Tab */}
          <TabsContent value="report" className="space-y-4 mt-4">
            {/* Week Navigation */}
            <div className="flex items-center justify-between border border-border rounded-lg p-2 bg-card">
              <Button variant="ghost" size="sm" onClick={handlePrevWeek}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button variant="ghost" className="font-medium">
                    <CalendarIcon className="h-4 w-4 mr-2" />
                    {format(currentWeek, 'd MMM', { locale })} - {format(addDays(currentWeek, 6), 'd MMM yyyy', { locale })}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar 
                    mode="single" 
                    selected={currentWeek} 
                    onSelect={handleDateSelect}
                    locale={locale}
                    weekStartsOn={1}
                    disabled={(date) => !isBefore(addDays(startOfWeek(date, { weekStartsOn: 1 }), 6), today)}
                  />
                </PopoverContent>
              </Popover>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleNextWeek}
                disabled={!isBefore(addDays(addWeeks(currentWeek, 1), 6), today)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>

            {/* Status Banner */}
            {isValidated && (
              <div className="flex items-center gap-3 p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg">
                <CheckCircle className="h-5 w-5 text-emerald-600" />
                <span className="text-emerald-700 dark:text-emerald-400">{t('weekValidated')}</span>
              </div>
            )}
            {isSubmitted && (
              <div className="flex items-center gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                <Clock className="h-5 w-5 text-amber-600" />
                <span className="text-amber-700 dark:text-amber-400">{t('weekPendingValidation')}</span>
              </div>
            )}
            {isRejected && (
              <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <X className="h-5 w-5 text-red-600" />
                <span className="text-red-700 dark:text-red-400">{t('weekRejected')}: {weekReporting?.rejection_reason}</span>
              </div>
            )}
            {!isPastWeek && (
              <div className="flex items-center gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <AlertCircle className="h-5 w-5 text-blue-600" />
                <span className="text-blue-700 dark:text-blue-400">{t('selectPastWeek')}</span>
              </div>
            )}

            {/* Week Summary */}
            <div className="grid grid-cols-3 gap-4">
              <div className="stat-card">
                <p className="stat-label">{t('plannedHours')}</p>
                <p className="stat-value">{weekTotals.planned}h</p>
              </div>
              <div className="stat-card">
                <p className="stat-label">{t('actualHours')}</p>
                <p className="stat-value text-primary">{weekTotals.actual}h</p>
              </div>
              <div className="stat-card">
                <p className="stat-label">{t('difference')}</p>
                <p className={`stat-value ${parseFloat(weekTotals.actual) - parseFloat(weekTotals.planned) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {calculateDifference(weekTotals.planned, weekTotals.actual)}
                </p>
              </div>
            </div>

            {/* Schedule Table */}
            {weekSchedule.length === 0 ? (
              <div className="border border-border rounded-lg p-8 text-center">
                <Clock className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">{t('noScheduleForWeek')}</p>
              </div>
            ) : (
              <div className="border border-border rounded-lg overflow-hidden">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>{t('date')}</th>
                      <th>{t('plannedHours')}</th>
                      <th>{t('actualHours')}</th>
                      <th>{t('difference')}</th>
                      <th>{t('comment')}</th>
                      <th>{t('status')}</th>
                      <th>{t('actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {weekSchedule.map((slot) => {
                      const plannedHours = calculateHours(slot.start_time, slot.end_time);
                      const actualHours = slot.actual_start && slot.actual_end 
                        ? calculateHours(slot.actual_start, slot.actual_end)
                        : plannedHours;
                      const hasChanges = slot.actual_start && (slot.actual_start !== slot.start_time || slot.actual_end !== slot.end_time);
                      
                      return (
                        <tr key={slot.id} className={hasChanges ? 'bg-amber-50/50 dark:bg-amber-900/10' : ''}>
                          <td className="font-medium">
                            {format(parseISO(slot.date), 'EEEE d', { locale })}
                          </td>
                          <td className="mono text-sm">
                            {slot.start_time} - {slot.end_time}
                            <span className="text-muted-foreground ml-2">({plannedHours}h)</span>
                          </td>
                          <td className="mono text-sm font-medium text-primary">
                            {slot.actual_start || slot.start_time} - {slot.actual_end || slot.end_time}
                            <span className="ml-2">({actualHours}h)</span>
                          </td>
                          <td className={`mono text-sm font-medium ${hasChanges ? 'text-amber-600' : ''}`}>
                            {calculateDifference(plannedHours, actualHours)}
                          </td>
                          <td className="max-w-[150px] truncate text-sm text-muted-foreground">
                            {slot.reporting_comment || '-'}
                          </td>
                          <td>
                            {hasChanges ? (
                              <Badge className="bg-amber-500 text-white rounded">{t('modified')}</Badge>
                            ) : (
                              <Badge variant="outline" className="rounded">{t('unchanged')}</Badge>
                            )}
                          </td>
                          <td>
                            {!isValidated && !isSubmitted && isPastWeek && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openHoursDialog(slot)}
                                data-testid={`edit-hours-${slot.id}`}
                              >
                                {t('editHours')}
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Submit Button */}
            {canSubmit && weekSchedule.length > 0 && (
              <div className="flex justify-end">
                <Button onClick={submitWeekForValidation} className="flex items-center gap-2">
                  <Send className="h-4 w-4" />
                  {t('submitForValidation')}
                </Button>
              </div>
            )}
          </TabsContent>

          {/* Archived Tab */}
          <TabsContent value="archived" className="space-y-4 mt-4">
            <div className="flex items-center gap-3 mb-4">
              <Archive className="h-5 w-5 text-muted-foreground" />
              <h2 className="text-lg font-medium">{t('validatedWeeks')}</h2>
            </div>

            {archivedWeeks.length === 0 ? (
              <div className="border border-border rounded-lg p-8 text-center">
                <Archive className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">{t('noArchivedWeeks')}</p>
              </div>
            ) : (
              <div className="border border-border rounded-lg overflow-hidden">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>{t('week')}</th>
                      <th>{t('plannedHours')}</th>
                      <th>{t('actualHours')}</th>
                      <th>{t('difference')}</th>
                      <th>{t('validatedOn')}</th>
                      <th>{t('status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {archivedWeeks.map((week) => (
                      <tr key={week.id}>
                        <td className="font-medium">
                          {format(parseISO(week.week_start), 'd MMM', { locale })} - {format(addDays(parseISO(week.week_start), 6), 'd MMM yyyy', { locale })}
                        </td>
                        <td className="mono">{week.planned_hours}h</td>
                        <td className="mono text-primary">{week.actual_hours}h</td>
                        <td className={`mono ${week.actual_hours - week.planned_hours >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {calculateDifference(week.planned_hours, week.actual_hours)}
                        </td>
                        <td className="text-sm text-muted-foreground">
                          {week.validated_at ? format(parseISO(week.validated_at), 'dd/MM/yyyy', { locale }) : '-'}
                        </td>
                        <td>
                          <Badge className="bg-emerald-500 text-white rounded">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            {t('validated')}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Edit Hours Dialog */}
        <Dialog open={hoursDialogOpen} onOpenChange={setHoursDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('editActualHours').toUpperCase()}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              {editingSlot && (
                <div className="p-3 bg-muted rounded-lg text-sm">
                  <p><strong>{t('date')}:</strong> {format(parseISO(editingSlot.date), 'EEEE d MMMM', { locale })}</p>
                  <p><strong>{t('plannedHours')}:</strong> {editingSlot.start_time} - {editingSlot.end_time}</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('actualStartTime')}</Label>
                  <Select value={actualHours.start} onValueChange={(v) => setActualHours({ ...actualHours, start: v })}>
                    <SelectTrigger className="mono"><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-[200px]">
                      {TIME_SLOTS.map(time => (<SelectItem key={time} value={time} className="mono">{time}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t('actualEndTime')}</Label>
                  <Select value={actualHours.end} onValueChange={(v) => setActualHours({ ...actualHours, end: v })}>
                    <SelectTrigger className="mono"><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-[200px]">
                      {TIME_SLOTS.map(time => (<SelectItem key={time} value={time} className="mono">{time}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>{t('comment')}</Label>
                <Textarea 
                  value={actualHours.comment}
                  onChange={(e) => setActualHours({ ...actualHours, comment: e.target.value })}
                  placeholder={t('optionalComment')}
                />
              </div>
              <Button onClick={saveActualHours} className="w-full">{t('save')}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ==================== MANAGER VIEW ====================
  return (
    <div className="space-y-6" data-testid="reporting-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3">
          <FileText className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-3xl">{t('reportingPlanning').toUpperCase()}</h1>
            <p className="text-muted-foreground text-sm">
              {pendingReports.length} {t('pendingValidations')}
            </p>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="pending" className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            {t('pendingValidations')} ({pendingReports.length})
          </TabsTrigger>
          <TabsTrigger value="all" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            {t('allReports')}
          </TabsTrigger>
        </TabsList>

        {/* Pending Validations Tab */}
        <TabsContent value="pending" className="space-y-4 mt-4">
          {pendingReports.length === 0 ? (
            <div className="border border-border rounded-lg p-8 text-center">
              <CheckCircle className="h-12 w-12 mx-auto text-emerald-500 mb-4" />
              <p className="text-muted-foreground">{t('noPendingValidations')}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {pendingReports.map((report) => (
                <div 
                  key={report.id}
                  className={`border rounded-lg p-4 ${report.has_modifications ? 'border-amber-300 bg-amber-50/50 dark:bg-amber-900/10' : 'border-border'}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <span 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: report.user_color || '#3B82F6' }}
                        />
                        <h3 className="font-medium">{report.user_name}</h3>
                        {report.has_modifications && (
                          <Badge className="bg-amber-500 text-white rounded text-xs">{t('modified')}</Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {format(parseISO(report.week_start), 'd MMM', { locale })} - {format(addDays(parseISO(report.week_start), 6), 'd MMM yyyy', { locale })}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleReviewReport(report.id, 'approved')}
                        className="bg-emerald-600 hover:bg-emerald-700"
                      >
                        <Check className="h-4 w-4 mr-1" />
                        {t('approve')}
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleReviewReport(report.id, 'rejected')}
                      >
                        <X className="h-4 w-4 mr-1" />
                        {t('reject')}
                      </Button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">{t('plannedHours')}:</span>
                      <span className="ml-2 font-medium">{report.planned_hours}h</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">{t('actualHours')}:</span>
                      <span className="ml-2 font-medium text-primary">{report.actual_hours}h</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">{t('difference')}:</span>
                      <span className={`ml-2 font-medium ${report.actual_hours - report.planned_hours >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {calculateDifference(report.planned_hours, report.actual_hours)}
                      </span>
                    </div>
                  </div>

                  {/* Slots with modifications */}
                  {report.slots?.filter(s => s.has_modification).length > 0 && (
                    <div className="mt-3 pt-3 border-t border-border">
                      <p className="text-sm font-medium mb-2">{t('modifiedSlots')}:</p>
                      <div className="space-y-1">
                        {report.slots.filter(s => s.has_modification).map((slot, idx) => (
                          <div key={idx} className="text-xs bg-amber-100 dark:bg-amber-900/30 p-2 rounded flex justify-between">
                            <span>{format(parseISO(slot.date), 'EEE d/MM', { locale })}</span>
                            <span>{slot.original_start}-{slot.original_end} → {slot.actual_start}-{slot.actual_end}</span>
                            {slot.comment && <span className="text-muted-foreground truncate max-w-[150px]">{slot.comment}</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* All Reports Tab */}
        <TabsContent value="all" className="space-y-4 mt-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-4 p-4 border border-border rounded-lg bg-card">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">{t('filters')}:</span>
            </div>
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); fetchAllReports(); }}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder={t('status')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('allStatuses')}</SelectItem>
                <SelectItem value="pending">{t('pending')}</SelectItem>
                <SelectItem value="approved">{t('approved')}</SelectItem>
                <SelectItem value="rejected">{t('rejected')}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={userFilter} onValueChange={(v) => { setUserFilter(v); fetchAllReports(); }}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder={t('collaborator')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('allCollaborators')}</SelectItem>
                {team.map(member => (
                  <SelectItem key={member.id} value={member.id}>{member.first_name} {member.last_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm">
                  <CalendarIcon className="h-4 w-4 mr-2" />
                  {dateFilter ? format(dateFilter, 'd MMM yyyy', { locale }) : t('allDates')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar 
                  mode="single" 
                  selected={dateFilter} 
                  onSelect={(date) => { setDateFilter(date); fetchAllReports(); }}
                  locale={locale}
                  weekStartsOn={1}
                />
              </PopoverContent>
            </Popover>
            {dateFilter && (
              <Button variant="ghost" size="sm" onClick={() => { setDateFilter(null); fetchAllReports(); }}>
                {t('clearDate')}
              </Button>
            )}
          </div>

          {/* Reports Table */}
          {allReports.length === 0 ? (
            <div className="border border-border rounded-lg p-8 text-center">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">{t('noReportsFound')}</p>
            </div>
          ) : (
            <div className="border border-border rounded-lg overflow-hidden">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t('collaborator')}</th>
                    <th>{t('week')}</th>
                    <th>{t('plannedHours')}</th>
                    <th>{t('actualHours')}</th>
                    <th>{t('difference')}</th>
                    <th>{t('status')}</th>
                    <th>{t('submittedOn')}</th>
                  </tr>
                </thead>
                <tbody>
                  {allReports.map((report) => (
                    <tr key={report.id} className={report.has_modifications ? 'bg-amber-50/30 dark:bg-amber-900/5' : ''}>
                      <td>
                        <div className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded-full" style={{ backgroundColor: report.user_color || '#3B82F6' }} />
                          <span className="font-medium">{report.user_name}</span>
                          {report.has_modifications && <AlertCircle className="h-3 w-3 text-amber-500" />}
                        </div>
                      </td>
                      <td className="text-sm">
                        {format(parseISO(report.week_start), 'd MMM', { locale })} - {format(addDays(parseISO(report.week_start), 6), 'd MMM', { locale })}
                      </td>
                      <td className="mono">{report.planned_hours}h</td>
                      <td className="mono text-primary">{report.actual_hours}h</td>
                      <td className={`mono ${report.actual_hours - report.planned_hours >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {calculateDifference(report.planned_hours, report.actual_hours)}
                      </td>
                      <td>
                        <Badge className={`rounded ${
                          report.status === 'approved' ? 'bg-emerald-500' : 
                          report.status === 'rejected' ? 'bg-red-500' : 
                          'bg-amber-500'
                        } text-white`}>
                          {t(report.status)}
                        </Badge>
                      </td>
                      <td className="text-sm text-muted-foreground">
                        {format(parseISO(report.submitted_at), 'dd/MM/yyyy HH:mm', { locale })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
